/* eslint-disable no-param-reassign */
import axios from 'axios';
import _get from 'lodash/get';
import _chunk from 'lodash/chunk';
import _isArray from 'lodash/isArray';
import _uniqBy from 'lodash/uniqBy';

import { lowerCase } from 'functions/lowerCase';
import { BASE_PATH_API } from 'constants/env';
import {
  DESKTOP_PAGE,
  DESKTOP_SIZE,
  MOBILE_PAGE,
  MOBILE_SIZE
} from 'constants/values/params';
import {
  decodeHtmlTitle,
  getProductsById,
  getMapProductWithTableInfo
} from 'services/api/products';
import { getPaginationProps, calculateTotalPages } from 'util/calculations';
import { HIGH_RATING } from 'constants/values/gifts';
import { isMobile } from 'util/screen';
import { transformProductData, getListOfPrices } from './transformers';
import { getProductsByCategory } from '../recommendations/index';

const CUSTOMER = 'sears_v2';
const STYLES_SHEET = 'xml2json';
const FORMAT = 'UTF-8';
const DS =
  'marcas:attribute_marca:0:0:8:0:1.sale_precio:sale_price:1:1:1000.ranking:review:0:0:8:0:1.full:fulfillment:0:0:8:0:1.free:shipping_price:0:0:8:0:1.discount:discount:0:0:1000:0:1';
const DO = 'breadcrumbs:breadcrumbs:id,name,padre:10:1';

const DEFAULT_PARAMS = {
  cliente: CUSTOMER,
  proxystylesheet: STYLES_SHEET,
  oe: FORMAT,
  getfields: '*',
  ds: DS,
  do: DO,
  start: 0
};

const PRODUCT_DISCOUNT = 'discount';
const PRODUCT_PRICE = 'sale_price';
const PRODUCT_REAL_PRICE = 'price';
const PRODUCT_IMAGE = 'link_thumbnail';

const BREADCRUMBS_TYPE = 'breadcrumbs';
const BRAND_TYPE = 'marcas';
const DISCOUNT_TYPE = 'discount';
const RANKING_TYPE = 'ranking';

const FILTERS_TO_USE = [
  BREADCRUMBS_TYPE,
  BRAND_TYPE,
  DISCOUNT_TYPE,
  RANKING_TYPE
];

const FILTERS_FIELD_NAMES = {
  [BREADCRUMBS_TYPE]: 'sections',
  [BRAND_TYPE]: 'brands',
  [DISCOUNT_TYPE]: 'discounts',
  [RANKING_TYPE]: 'rankings'
};

const FIELDS_TO_FILTER = ['sections', 'brands'];

const LIMIT = 20;

const transformParams = ({
  size,
  page,
  mobileSize = MOBILE_SIZE,
  mobilePage = MOBILE_PAGE,
  category,
  section,
  brand,
  sort,
  highRating,
  minPrice,
  maxPrice,
  discount,
  stockStart = false,
  start = 0
} = {}) => {
  const sectionField = section ? `.categories->id:${section}` : '';
  const requiredobjects = `${category ? `categories->id:${category}` : ''}${
    sectionField || ''
  }`;

  const brandField = brand ? `attribute_marca:${lowerCase(brand)}.` : '';
  const sortField = sort ? `meta:${sort}` : '';
  const ratingField = highRating ? `${HIGH_RATING}.` : '';
  const minPriceField = minPrice ? `sale_price>=${minPrice}.` : '';
  const maxPriceField = maxPrice ? `sale_price<=${maxPrice}.` : '';
  const discountField = discount ? `discount>=${discount}.` : '';

  let requiredFields = `${brandField}${ratingField}${minPriceField}${maxPriceField}${discountField}`;
  requiredFields = requiredFields.substring(0, requiredFields.length - 1);

  const hasMobile = isMobile({ largeMobile: true });

  const startPaginated = size * (page - 1);
  const startValue = stockStart ? start : startPaginated;
  const mobileSizeValue = mobileSize * mobilePage;

  return {
    start: hasMobile ? 0 : startValue,
    num: hasMobile ? mobileSizeValue : size,
    requiredobjects,
    requiredfields: requiredFields,
    sort: sortField
  };
};

/* Regresa los datos filtrados */
export const getGiftsFilters = async (filterParams, onlyFilters = false) => {
  const { mobileSize = MOBILE_SIZE } = filterParams;
  const transformedParams = transformParams(filterParams);
  const hasMobile = isMobile({ largeMobile: true });

  const params = {
    ...DEFAULT_PARAMS,
    ...transformedParams
  };

  const url = `${BASE_PATH_API}/anteater/search`;

  const response = await axios({ url, params });

  const totalElements = Number(_get(response, 'data.GSP.RES.M', 1));

  const totalPages = calculateTotalPages(
    totalElements,
    hasMobile ? filterParams.mobileSize : filterParams.size
  );

  let tmpList = _get(response, 'data.GSP.RES.R', []);

  if (!_isArray(tmpList) && tmpList.T) {
    tmpList = [tmpList];
  }

  const listOfProducts = tmpList.map((product) => {
    const { MT: productData = [] } = product;

    const reduceProductData = (acc, current) => {
      acc[current.N] = current.V;
      return acc;
    };

    const transformedProductData = productData.reduce(reduceProductData, {});
    return {
      ...product,
      ...transformedProductData,
      title: decodeHtmlTitle(transformedProductData.title),
      c_descuento: Number(transformedProductData[PRODUCT_DISCOUNT]),
      price: Number(transformedProductData[PRODUCT_PRICE]),
      msrp: Number(transformedProductData[PRODUCT_REAL_PRICE]),
      image: transformedProductData[PRODUCT_IMAGE],
      stock: transformedProductData.stock || 1, // TODO: Add stock to search product query
      featured: true
    };
  });

  if (!onlyFilters) {
    // Set real stock, variants & more
    // En este punto ya esta conectado el API de Seras, agregar lo que vaya faltando
    if (listOfProducts.length === 0) throw new Error('List of products has empty');

    const allProductIds = listOfProducts.map((g) => g.id);

    const chunkAllProductIds = _chunk(allProductIds, LIMIT);

    const listProductData = await Promise.all(
      chunkAllProductIds.map((list) => getProductsById(list, {}, true))
    );

    // eslint-disable-next-line prefer-spread
    const productsData = [].concat.apply([], listProductData);

    // const productsData = await getProductsById(listOfProducts.map((g) => g.id));

    listOfProducts.map((product) => {
      const productFilter = productsData.filter(
        (r) => +r.id === +product.id
      );
      const productFound = productFilter.length ? productFilter[0] : null;
      if (productFound) {
        product.stock = productFound?.stock || 0;
        product.variations = productFound?.variations || null;
        product.status = productFound?.is_active || productFound?.status || false;
      }
      return product;
    });
  }

  const listOfFilters = _get(response, 'data.GSP.RES.PARM.PMT', [])
    .filter((data) => {
      const hasFilterToUse = FILTERS_TO_USE.some(
        (filter) => filter === data.DN
      );
      return hasFilterToUse;
    })
    .map((filter) => {
      const filterList = _get(filter, 'PV', []);
      const filterName = _get(filter, 'DN', '');

      const list = _isArray(filterList)
        ? filterList.map((filterValue) => {
            const id = filterValue.id || filterValue.C;
            const name = filterValue.name || filterValue.V || filterValue.C;
            const value = filterValue.V || filterValue.C;
            return { id, value, name };
          })
        : filterList.V
        ? [
            {
              id: filterList.id || filterList.V,
              value: filterList.V,
              name: filterList.name || filterList.V
            }
          ]
        : [];

      const isBrand = filterName === BRAND_TYPE;
      const formattedList = list.map((current) => {
        if (isBrand) {
          current.name = current.name.toLowerCase();
          current.value = current.name.toLowerCase();
        }
        return current;
      });

      return {
        ...filter,
        name: filter.DN,
        list: formattedList
      };
    })
    .filter((filter) => filter.list.length > 0)
    .reduce((filtersAcc, currentFilter) => {
      const field = FILTERS_FIELD_NAMES[currentFilter.name];

      let list = _get(currentFilter, 'list', []);

      if (FIELDS_TO_FILTER.includes(field)) {
        list = _uniqBy(list, 'name');
      }

      filtersAcc[field] = list;

      return filtersAcc;
    }, {});

  const productInfo = _get(response, 'data.GSP.RES.PARM.PMT', []);
  const productsPriceInfo = productInfo?.map((info) => info.PV);
  const listOfPrices = getListOfPrices(productsPriceInfo);

  const mobileList = hasMobile
    ? listOfProducts
    : listOfProducts.slice(0, mobileSize);

  
  // console.log('listOfFilters: ',listOfFilters)

  return {
    totalPages,
    totalElements,
    list: listOfProducts,
    mobileList,
    listOfProducts,
    filters: listOfFilters,
    prices: listOfPrices
  };
};


export const getGiftsCategories = async () => {
  // const url = `${BASE_PATH_API}/app/ApiRest/Categorias`;
  const url = 'https://seapi.sears.com.mx/categories/v1/category/tree';
  const response = await axios({ url });
  return response.data.data;
};

export const getProductDetails = async (id) => {
  const url = `${BASE_PATH_API}/app/v1/product/${id}`;
  const response = await axios({ url });

  const productData = transformProductData(response.data.data);

  return productData;
};

export const getProductsWithPagination = async ({
  allProducts,
  params = {},
  tableInfoProducts,
  productsToMatch = [],
  isExternal = false
} = {}) => {
  const size = _get(params, 'size', DESKTOP_SIZE);
  const page = _get(params, 'page', DESKTOP_PAGE);
  const mobileSize = _get(params, 'mobileSize', MOBILE_SIZE);
  const mobilePage = _get(params, 'mobilePage', MOBILE_PAGE);

  const paginationProps = getPaginationProps(
    allProducts,
    size,
    page,
    mobileSize,
    mobilePage
  );

  const productsParams = {
    size,
    page: 1,
    mobileSize,
    mobilePage
  };

  const productsToFetch = isMobile()
    ? paginationProps.mobileList
    : paginationProps.list;

  const variants = tableInfoProducts
    .map((tip) => tip.variant_id)
    .filter((v) => v);

  const { list, mobileList } = await getProductsByCategory(
    { products: productsToFetch },
    productsParams,
    { variants },
    { isExternal }
  );

  paginationProps.list = list.map(
    getMapProductWithTableInfo(tableInfoProducts || productsToMatch)
  );
  paginationProps.mobileList = mobileList.map(
    getMapProductWithTableInfo(tableInfoProducts || productsToMatch)
  );
  paginationProps.allElements = isMobile() ? mobileList : list;

  return paginationProps;
};
